import { RJSFSchema } from '@rjsf/utils';

import {
  FilingInstitutionNonTinIdentificationTypeCode,
  FilingInstitutionOrganizationSubtypeCodes,
  MAILING_ADDRESS,
  TIN,
} from 'dashboard/views/DashboardSARShowView/FormSAR/constants';
import { HorizontalLayoutObjectFieldTemplate } from 'dashboard/views/DashboardSARShowView/FormSAR/rjsf/FormComponents/HorizontalLayoutObjectFieldTemplate';

interface SARConfigurationSection {
  title: string;
  schema: RJSFSchema;
  uiSchema?: any; // TODO: type this with generics
}

type SAROrganizationConfiguration = Record<string, SARConfigurationSection>;

export const sarOrganizationConfiguration: SAROrganizationConfiguration = {
  financialInstitutionIdentification: {
    title: 'Organization identifier',
    schema: {
      type: 'object',
      properties: {
        partyIdentificationTypeCode: {
          title: 'ID type',
          type: 'number',
          oneOf: FilingInstitutionNonTinIdentificationTypeCode,
        },
        partyIdentificationNumberText: {
          title: 'Number',
          type: 'string',
          maxLength: 10,
          pattern: '^[0-9]*$',
        },
      },
    },
    uiSchema: {
      'ui:ObjectFieldTemplate': HorizontalLayoutObjectFieldTemplate,
    },
  },
  filingInstitution: {
    title: 'Filing institution',
    schema: {
      type: 'object',
      properties: {
        filingInstitutionContactInformation: {
          type: 'object',
          properties: {
            typeOfFinancialInstitution: {
              type: 'object',
              properties: {
                organizationTypeId: {
                  type: 'number',
                  title: 'Type of financial institution',
                  oneOf: [
                    { const: 1, title: 'Casino/Card Club' },
                    { const: 2, title: 'Depository Institution' },
                    { const: 3, title: 'Insurance company' },
                    { const: 4, title: 'MSB (Money Service Business)' },
                    { const: 5, title: 'Securities/Futures' },
                    { const: 11, title: 'Loan or Finance Company' },
                    { const: 12, title: 'Housing GSE (Government Sponsored Enterprise)' },
                    { const: 999, title: 'Other' },
                  ],
                },
              },
              required: ['organizationTypeId'],
              allOf: [
                {
                  if: {
                    $comment: 'if other is selected, show other text input',
                    properties: { organizationTypeId: { const: 999 } },
                    required: ['organizationTypeId'],
                  },
                  then: {
                    properties: {
                      otherOrganizationTypeText: {
                        type: 'string',
                        title: 'Please specify other',
                      },
                    },
                    required: ['otherOrganizationTypeText'],
                  },
                },
                {
                  if: {
                    $comment: 'if securities/futures is selected show subtype selection',
                    properties: { organizationTypeId: { const: 5 } },
                    required: ['organizationTypeId'],
                  },
                  then: {
                    properties: {
                      primaryRegulatorTypeCodeSecuritiesAndFutures: {
                        title: 'Primary federal regulator',
                        type: 'number',
                        oneOf: [
                          { const: 9, title: 'CFTC (Commodities Futures Trading Commission)' },
                          { const: 6, title: 'SEC (Securities and Exchange Commission)' },
                        ],
                      },
                      organizationSubtypeId: {
                        title: 'Type of Securities and Futures Institution',
                        description: 'Select all that apply',
                        type: 'array',
                        uniqueItems: true,
                        items: {
                          type: 'number',
                          oneOf: FilingInstitutionOrganizationSubtypeCodes,
                        },
                        default: [],
                      },
                    },
                    required: [
                      'organizationSubtypeId',
                      'primaryRegulatorTypeCodeSecuritiesAndFutures',
                    ],
                    if: {
                      $comment: 'if other is selected, show other text input',
                      properties: { organizationSubtypeId: { contains: { const: 5999 } } },
                      required: ['organizationSubtypeId'],
                    },
                    then: {
                      properties: {
                        otherOrganizationSubTypeText: {
                          title: 'Other',
                          type: 'string',
                        },
                      },
                      required: ['otherOrganizationSubTypeText'],
                    },
                  },
                },
                {
                  if: {
                    $comment:
                      'if depository institution is selected, show related primary regulator type',
                    properties: {
                      organizationTypeId: { const: 2 },
                    },
                    required: ['organizationTypeId'],
                  },
                  then: {
                    properties: {
                      primaryRegulatorTypeCodeDepository: {
                        title: 'Primary federal regulator',
                        type: 'number',
                        oneOf: [
                          { const: 2, title: 'FDIC (Federal Deposit Insurance Corporation)' },
                          { const: 13, title: 'FHFA (Federal Housing Finance Agency)' },
                          { const: 7, title: 'IRS (Internal Revenue Service)' },
                          { const: 3, title: 'NCUA (National Credit Union Administration)' },
                          { const: 4, title: 'OCC (Office of the Comptroller of the Currency)' },
                        ],
                      },
                    },
                    required: ['primaryRegulatorTypeCodeDepository'],
                  },
                },
                {
                  if: {
                    $comment:
                      'if casino/card club, insurance company or MSB is selected, only show IRS as option',
                    properties: {
                      organizationTypeId: { anyOf: [{ const: 1 }, { const: 3 }, { const: 4 }] },
                    },
                    required: ['organizationTypeId'],
                  },
                  then: {
                    properties: {
                      primaryRegulatorTypeCodeIRS: {
                        title: 'Primary federal regulator',
                        type: 'number',
                        oneOf: [{ const: 7, title: 'IRS (Internal Revenue Service)' }],
                      },
                    },
                    required: ['primaryRegulatorTypeCodeIRS'],
                  },
                },
                {
                  if: {
                    properties: {
                      organizationTypeId: { anyOf: [{ const: 11 }, { const: 12 }, { const: 999 }] },
                    },
                    required: ['organizationTypeId'],
                  },
                  then: {
                    properties: {
                      primaryRegulatorTypeCode: {
                        title: 'Primary federal regulator',
                        type: 'number',
                        oneOf: [
                          { const: 9, title: 'CFTC (Commodities Futures Trading Commission)' },
                          { const: 1, title: 'Federal Reserve ' },
                          { const: 2, title: 'FDIC (Federal Deposit Insurance Corporation)' },
                          { const: 13, title: 'FHFA (Federal Housing Finance Agency)' },
                          { const: 7, title: 'IRS (Internal Revenue Service)' },
                          { const: 3, title: 'NCUA (National Credit Union Administration)' },
                          { const: 4, title: 'OCC (Office of the Comptroller of the Currency)' },
                          { const: 6, title: 'SEC (Securities and Exchange Commission)' },
                          { const: 99, title: 'Not Applicable' },
                        ],
                      },
                    },
                    required: ['primaryRegulatorTypeCode'],
                  },
                },
              ],
            },
            filerName: {
              type: 'object',
              properties: {
                rawPartyFullName: {
                  title:
                    'Filer name (Holding company, lead financial institution, or agency, if applicable)',
                  type: 'string',
                  maxLength: 150,
                },
              },
              required: ['rawPartyFullName'],
            },
            tin: TIN,
            contactOffice: {
              type: 'object',
              properties: {
                rawPartyFullName: {
                  title: 'Contact office name',
                  type: 'string',
                  maxLength: 150,
                },
              },
              required: ['rawPartyFullName'],
            },
            contactPhoneNumber: {
              type: 'object',
              properties: {
                phoneNumberText: {
                  title: 'Contact phone number (include area code)',
                  type: 'string',
                  maxLength: 16,
                  // skipped not all same digit validation
                  pattern: '^[0-9]*$',
                },
                phoneNumberExtensionText: {
                  title: 'Ext.',
                  type: 'string',
                  maxLength: 6,
                  pattern: '^[0-9]*$',
                },
              },
              required: ['phoneNumberText'],
            },
            mailingAddress: MAILING_ADDRESS,
            alternateName: {
              properties: {
                rawPartyFullName: {
                  title: 'Alternate name, e.g., AKA - individual or trade name, DBA - entity',
                  type: 'string',
                  maxLength: 150,
                },
              },
            },
            internalControlNumber: {
              properties: {
                partyIdentificationNumberText: {
                  title: 'Internal control number',
                  type: 'string',
                  maxLength: 20,
                },
              },
            },
          },
        },
      },
    },
  },
};
